import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { notification, Typography } from 'antd'
import clsx from 'clsx'
import useDirectory from 'hooks/useDirectory'
import { useRecoilState, useRecoilValue } from 'recoil'
import notificationApiAtom from 'store/antdContextStore'
import {
  buildingModeState,
  SelectedMode,
  setAnnotationModeState
} from 'store/buildingStore'
import Krpano from 'utils/krpano'

import SetAnnoCreateIcon from 'components/icons/SetAnnoCreateIcon'
import SetAnnoPlacingIcon from 'components/icons/SetAnnoPlacingIcon'
import SetAnnoSearchIcon from 'components/icons/SetAnnoSearchIcon'

import BCPModalAddSetAnnotation from '../BCPSetAnnotation/BCPModalAddSetAnnotation'

import ModalAddSetAnnotation from './ModalAddSetAnnotation'
import ModalDeleteSetAnnotation from './ModalDeleteSetAnnotation'
import ModalListSetAnnotation from './ModalListSetAnnotation'

type Props = {
  isCheckLockedPoint: boolean
}

export default function MenuSetAnnotation({ isCheckLockedPoint }: Props) {
  const { t } = useTranslation()
  const { getDirectory } = useDirectory()

  const [isExpanding, setIsExpanding] = useState(false)
  const [annotationPosition, setAnnotationPosition] = useState<string[]>([])
  const [{ mode }, setSelectedMode] = useRecoilState(setAnnotationModeState)

  const [buildingMode, setBuildingMode] = useRecoilState(buildingModeState)
  const [isSelectAnnotationPos, setIsSelectAnnotationPos] = useState(
    buildingMode.selectedMode === SelectedMode.PLACING_SET_ANNOTATION
  )

  const notificationApi = useRecoilValue(notificationApiAtom)
  const cleanupTempAnnotation = useCallback(() => {
    setAnnotationPosition([])
    Krpano.RemoveTempAnnotation()
  }, [])

  const handleSelectMode = useCallback(
    (mode: string | undefined) => {
      if (['placingSetAnnotation', 'addSetAnnotation'].includes(mode || '')) {
        cleanupTempAnnotation()
      }
      setBuildingMode((prev) => ({ ...prev, selectedMode: SelectedMode.NONE }))
      setSelectedMode((prev) => ({ ...prev, mode }))
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setBuildingMode, setSelectedMode]
  )

  const handleCancelPlacing = useCallback(() => {
    setIsSelectAnnotationPos(false)
    handleSelectMode(undefined)
    setAnnotationPosition([])
    notification.destroy('placingSetAnnotation')
    Krpano.RemoveTempAnnotation()
  }, [handleSelectMode])

  useEffect(() => {
    if (
      buildingMode.selectedMode !== SelectedMode.PLACING_SET_ANNOTATION &&
      buildingMode.selectedMode !== ''
    ) {
      setIsSelectAnnotationPos(false)
    }

    if (buildingMode.selectedMode === SelectedMode.PLACING_SET_ANNOTATION) {
      notificationApi?.success({
        message: t('translation.addSetAnnotationGuide'),
        duration: 3
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buildingMode.selectedMode])

  const handleTogglePlacingSetAnnotation = () => {
    if (!isCheckLockedPoint) return
    if (isSelectAnnotationPos) {
      handleCancelPlacing()
      return
    }
    setIsSelectAnnotationPos(true)
    handleSelectMode(SelectedMode.PLACING_SET_ANNOTATION)
    setBuildingMode((prev) => ({
      ...prev,
      selectedMode: SelectedMode.PLACING_SET_ANNOTATION
    }))
  }

  const handleToggleCreatingSetAnnotation = () => {
    if (!isCheckLockedPoint) return
    handleSelectMode(SelectedMode.ADD_SET_ANNOTATION)
    setBuildingMode((prev) => ({
      ...prev,
      selectedMode: SelectedMode.ADD_SET_ANNOTATION
    }))
  }

  const handleToggleModalListSetAnnotation = () => {
    if (!isCheckLockedPoint) return
    handleSelectMode(SelectedMode.LIST_SET_ANNOTATION)
    setBuildingMode((prev) => ({
      ...prev,
      selectedMode: SelectedMode.LIST_SET_ANNOTATION
    }))
  }

  const getSetAnnotationPosition = useCallback(() => {
    ;(window as any).get_set_annotation_position = () => {
      if (isSelectAnnotationPos && isCheckLockedPoint) {
        const krpano = document.getElementById('embedpano-full') as any
        const h = krpano.get('mouseath')
        const v = krpano.get('mouseatv')
        setAnnotationPosition((prev: any) => [...prev, h, v])
        if (!annotationPosition?.length) {
          krpano.call(
            `set(hotspot[temp_circle_style].visible,true);
                 set(hotspot[temp_circle_style].ath,${h});
                 set(hotspot[temp_circle_style].atv,${v});`
          )
          krpano.call(
            `set(hotspot[temp_polyline_style].visible,true);
                 set(hotspot[temp_polyline_style].point[0].ath,${h});
                 set(hotspot[temp_polyline_style].point[0].atv,${v});`
          )
        }
        if (annotationPosition?.length === 2) {
          krpano.call(
            `set(hotspot[temp_polyline_style].point[1].ath,${h});
                 set(hotspot[temp_polyline_style].point[1].atv,${v});`
          )
          krpano.call(
            `set(hotspot[temp_circle_middle_style].visible,true);
                 set(hotspot[temp_circle_middle_style].ath,${h});
                 set(hotspot[temp_circle_middle_style].atv,${v});`
          )
          krpano.call(
            `set(hotspot[temp_polyline_style2].visible,true);
                 set(hotspot[temp_polyline_style2].point[0].ath,${h});
                 set(hotspot[temp_polyline_style2].point[0].atv,${v});`
          )
        }
        if (annotationPosition?.length === 4) {
          krpano.call(
            `set(hotspot[temp_polyline_style2].point[1].ath,${h});
                 set(hotspot[temp_polyline_style2].point[1].atv,${v});`
          )
        }
      }
    }
  }, [annotationPosition?.length, isCheckLockedPoint, isSelectAnnotationPos])

  useEffect(() => {
    getSetAnnotationPosition()
  }, [getSetAnnotationPosition])

  useEffect(() => {
    if (!buildingMode.previewMode || !isCheckLockedPoint) {
      handleCancelPlacing()
    }
  }, [
    buildingMode.previewMode,
    handleCancelPlacing,
    isCheckLockedPoint,
    setSelectedMode
  ])

  useEffect(() => {
    if (!buildingMode.previewMode && isExpanding) {
      setIsExpanding(false)
    }
  }, [buildingMode.previewMode, isExpanding])

  useEffect(() => {
    const historyToggler = document.getElementById('version-history-toggler')
    const pointActionsMenuContainer = document.getElementById(
      'point-action-menu-rightest-element'
    )
    if (!historyToggler || !pointActionsMenuContainer) return

    const togglerBound = historyToggler.getBoundingClientRect()
    const containerBound = pointActionsMenuContainer.getBoundingClientRect()

    if (containerBound.right + 5 >= togglerBound.left) {
      historyToggler.style.top = '98px'
      historyToggler.style.left = '-82px'
    }
    if (containerBound.right + 35 < togglerBound.left) {
      historyToggler.style.top = '20px'
      historyToggler.style.left = '-112px'
    }
  }, [isExpanding])

  if (!buildingMode.previewMode) return null

  return (
    <>
      <div className="flex justify-center items-center gap-[10px] px-4 h-[60px] rounded-[30px] bg-[#021120CC]">
        <div
          className={clsx(
            'w-[50px] flex flex-col items-center justify-between cursor-pointer',
            {
              'cursor-not-allowed opacity-60': !isCheckLockedPoint
            }
          )}
          onClick={handleToggleCreatingSetAnnotation}
        >
          <SetAnnoCreateIcon
            selected={mode === SelectedMode.ADD_SET_ANNOTATION}
          />
          <Typography
            className={`text-center font-bold text-[10px] leading-[10px] ${
              mode === SelectedMode.ADD_SET_ANNOTATION
                ? 'theme-text-sub-primary'
                : 'text-white'
            }`}
          >
            {t('translation.setAnnoCreate')}
          </Typography>
        </div>

        <div
          className={clsx(
            'w-[50px] flex flex-col items-center justify-between cursor-pointer',
            {
              'cursor-not-allowed opacity-60': !isCheckLockedPoint
            }
          )}
          onClick={handleToggleModalListSetAnnotation}
        >
          <SetAnnoSearchIcon
            selected={mode === SelectedMode.LIST_SET_ANNOTATION}
          />

          <Typography
            className={`text-center font-bold text-[10px] leading-[10px] ${
              mode === SelectedMode.LIST_SET_ANNOTATION
                ? 'theme-text-sub-primary'
                : 'text-white'
            }`}
          >
            {t('translation.setAnnoSearch')}
          </Typography>
        </div>

        {/* add set anotation button */}
        <div
          className={clsx(
            'w-[50px] flex flex-col items-center justify-between cursor-pointer',
            {
              'cursor-not-allowed opacity-60': !isCheckLockedPoint
            }
          )}
          onClick={handleTogglePlacingSetAnnotation}
        >
          <SetAnnoPlacingIcon
            selected={mode === SelectedMode.PLACING_SET_ANNOTATION}
          />
          <Typography
            className={`text-center font-bold text-[10px] leading-[10px] ${
              mode === SelectedMode.PLACING_SET_ANNOTATION
                ? 'theme-text-sub-primary'
                : 'text-white'
            }`}
          >
            {t('translation.setAnnoPlacing')}
          </Typography>
        </div>
      </div>

      {mode === SelectedMode.ADD_SET_ANNOTATION && getDirectory() !== 'bcp' && (
        <ModalAddSetAnnotation />
      )}

      {mode === SelectedMode.ADD_SET_ANNOTATION && getDirectory() === 'bcp' && (
        <BCPModalAddSetAnnotation />
      )}

      {(mode === SelectedMode.LIST_SET_ANNOTATION ||
        mode === SelectedMode.PLACING_SET_ANNOTATION ||
        mode === SelectedMode.EDIT_SET_ANNOTATION) && (
        <ModalListSetAnnotation
          annotationPosition={annotationPosition}
          setAnnotationPosition={setAnnotationPosition}
        />
      )}

      {mode === SelectedMode.DELETE_SET_ANNOTATION && (
        <ModalDeleteSetAnnotation />
      )}
    </>
  )
}
